// ─────────────────────────────────────────────────────────────────
// Glass-i — Purchase Return ITC Reversals page (Stage 6P.8).
//
// Server-paginated read-only register at
// /erp/purchase-returns/itc-reversals.
//
// Columns (brief §K4):
//   Debit Note No · Return No · Supplier · Supplier GSTIN
//   Original Bill · Taxable Reversal · CGST Reversal · SGST Reversal
//   IGST Reversal · Total ITC Reversal · Status
//
// Toolbar:
//   Date range (debit_note_date) · Supplier GSTIN search ·
//   Clear · Refresh
//
// Row interactions:
//   • Double-click → navigate to the parent return at
//                    /erp/purchase-returns/:purchase_return_id
//   • Debit Note No → same navigation
//
// Backend remains the authority for ALL ITC math:
//   reversal amounts are derived server-side at post time against the
//   purchase_gst_itc_register row that originally claimed the credit
//   for the linked purchase_bill_line. CANCELLED rows are flipped by
//   the cancel handler — never edited from the UI.
//
// Style:
//   Mirrors PurchaseReturnRegisterPage — slate-50 header strip,
//   22 px row density, sticky toolbar. Reuses the shared
//   GoodsReceiptsPage.css via the .grn-* wrapper so no new CSS file
//   is added.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, DatePicker, Input,
  Space, Spin, Table, Tag, Tooltip, message
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, BankOutlined
} from '@ant-design/icons';

import { purchaseReturnApi } from './purchaseReturnApi';
import {
  reversalStatusLabel,
  reversalStatusColor
} from './purchaseReturnHelpers';
import useAuth from '../../../hooks/useAuth';
import '../purchase/GoodsReceiptsPage.css';

const { RangePicker } = DatePicker;


// ─────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS  = ['25', '50', '100'];
const DATE_FMT           = 'DD-MMM-YYYY';
const ISO_FMT            = 'YYYY-MM-DD';
const SEARCH_DEBOUNCE_MS = 250;


// ─────────────────────────────────────────────────────────────────
// Pure helpers — kept local so the page doesn't depend on a shared
// formatter module that could drift.
// ─────────────────────────────────────────────────────────────────

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day   = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year  = d.getFullYear();
  return `${day}-${month}-${year}`;
}

// ₹ grouping is locale-aware (en-IN) so 12,34,567.89 is rendered the
// same way as elsewhere on the control plane. Falsy / non-finite
// inputs collapse to an em-dash so empty rows don't read as "₹0".
function fmtMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  try {
    return n.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  } catch {
    return n.toFixed(2);
  }
}

function pickField(row, snake, camel) {
  if (!row) return undefined;
  if (camel in row && row[camel] !== undefined && row[camel] !== null) return row[camel];
  if (snake in row && row[snake] !== undefined && row[snake] !== null) return row[snake];
  return undefined;
}

function errMessage(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

// Total reversal — prefer the server-emitted total_itc_reversal but
// derive from the parts when only the legs are present. Mirrors the
// pattern used in the register page for total_tax.
function readTotalReversal(row) {
  if (!row) return null;
  const total = pickField(row, 'total_itc_reversal', 'totalItcReversal');
  if (total !== undefined && total !== null && total !== '') {
    const n = Number(total);
    if (Number.isFinite(n)) return n;
  }
  const c = Number(pickField(row, 'cgst_reversal', 'cgstReversal') || 0);
  const s = Number(pickField(row, 'sgst_reversal', 'sgstReversal') || 0);
  const i = Number(pickField(row, 'igst_reversal', 'igstReversal') || 0);
  return (Number.isFinite(c) ? c : 0)
       + (Number.isFinite(s) ? s : 0)
       + (Number.isFinite(i) ? i : 0);
}


// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export default function PurchaseReturnItcReversalsPage() {
  const navigate = useNavigate();
  const { me } = useAuth();

  // Wildcard '*' is the master role; explicit
  // `erp.purchase.returns.*` strings are the Stage 6P.8 perms.
  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set  = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);

  const canView        = hasPerm('erp.purchase.returns.view');
  const canViewReports = hasPerm('erp.purchase.returns.reports.view');

  // ── Toolbar state ────────────────────────────────────────────
  const [gstinInput, setGstinInput] = useState('');
  const [gstin,      setGstin]      = useState('');
  const [dateRange,  setDateRange]  = useState(null);

  // ── Server pagination state ──────────────────────────────────
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total,    setTotal]    = useState(0);

  // ── Data state ───────────────────────────────────────────────
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);

  // ── Debounced GSTIN search ───────────────────────────────────
  const gstinDebounceRef = useRef(null);
  useEffect(() => {
    if (gstinDebounceRef.current) clearTimeout(gstinDebounceRef.current);
    gstinDebounceRef.current = setTimeout(() => {
      setGstin((prev) => {
        if (prev === gstinInput) return prev;
        setPage(1);
        return gstinInput;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (gstinDebounceRef.current) clearTimeout(gstinDebounceRef.current);
    };
  }, [gstinInput]);

  // ── Load ─────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!canView && !canViewReports) return;
    setLoading(true);
    try {
      const params = { page, pageSize };

      const gstinNeedle = (gstin || '').trim();
      if (gstinNeedle) {
        params.supplierGstin = gstinNeedle;
        params.gstin         = gstinNeedle;
      }
      if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
        params.fromDate = dateRange[0].format
          ? dateRange[0].format(ISO_FMT)
          : new Date(dateRange[0]).toISOString().slice(0, 10);
        params.toDate   = dateRange[1].format
          ? dateRange[1].format(ISO_FMT)
          : new Date(dateRange[1]).toISOString().slice(0, 10);
      }

      const out = await purchaseReturnApi.listItcReversals(params);
      const list = Array.isArray(out) ? out
        : Array.isArray(out?.rows) ? out.rows
        : Array.isArray(out?.data) ? out.data
        : Array.isArray(out?.items) ? out.items
        : [];
      const count = Array.isArray(out) ? list.length
        : Number(out?.total ?? out?.count ?? list.length) || 0;

      setRows(list);
      setTotal(count);
    } catch (e) {
      message.error({
        content: errMessage(e, 'Failed to load ITC reversal register.'),
        key: 'pr-itc'
      });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [canView, canViewReports, page, pageSize, gstin, dateRange]);

  useEffect(() => { load(); }, [load]);

  // ── Toolbar handlers — any filter change resets page to 1 ────
  const onDates = (v) => { setDateRange(v || null); setPage(1); };
  const onReset = () => {
    setGstinInput('');
    setGstin('');
    setDateRange(null);
    setPage(1);
  };

  // ── Row navigation — open the parent return ──────────────────
  const openParent = useCallback((row) => {
    const id = pickField(row, 'purchase_return_id', 'purchaseReturnId');
    if (!id) return;
    navigate(`/erp/purchase-returns/${encodeURIComponent(id)}`);
  }, [navigate]);

  // ── Columns ──────────────────────────────────────────────────
  const columns = useMemo(() => ([
    {
      title:     'Debit Note No',
      dataIndex: 'debitNoteNo',
      width:     180,
      render: (_v, row) => {
        const no = pickField(row, 'debit_note_no', 'debitNoteNo') || '—';
        return (
          <button
            type="button"
            className="grn-link"
            onClick={() => openParent(row)}
          >
            {no}
          </button>
        );
      }
    },
    {
      title:     'Debit Note Date',
      dataIndex: 'debitNoteDate',
      width:     140,
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtDate(pickField(row, 'debit_note_date', 'debitNoteDate'))}
        </span>
      )
    },
    {
      title:     'Return No',
      dataIndex: 'returnNo',
      width:     180,
      render: (_v, row) => {
        const v = pickField(row, 'return_no', 'returnNo');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Supplier',
      dataIndex: 'supplierName',
      render: (_v, row) => {
        const name = pickField(row, 'supplier_name', 'supplierName') || '—';
        return (
          <Tooltip title={name}>
            <span className="grn-supplier__name">{name}</span>
          </Tooltip>
        );
      }
    },
    {
      title:     'Supplier GSTIN',
      dataIndex: 'supplierGstin',
      width:     180,
      render: (_v, row) => {
        const v = pickField(row, 'supplier_gstin', 'supplierGstin');
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tag color="blue" className="grn-gstin-tag">{v}</Tag>
        );
      }
    },
    {
      title:     'Original Bill',
      dataIndex: 'supplierInvoiceNo',
      width:     180,
      render: (_v, row) => {
        const v = pickField(row, 'supplier_invoice_no', 'supplierInvoiceNo');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Taxable Reversal',
      dataIndex: 'taxableReversal',
      width:     140,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {fmtMoney(pickField(row, 'taxable_reversal', 'taxableReversal'))}
        </span>
      )
    },
    {
      title:     'CGST Reversal',
      dataIndex: 'cgstReversal',
      width:     130,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {fmtMoney(pickField(row, 'cgst_reversal', 'cgstReversal'))}
        </span>
      )
    },
    {
      title:     'SGST Reversal',
      dataIndex: 'sgstReversal',
      width:     130,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {fmtMoney(pickField(row, 'sgst_reversal', 'sgstReversal'))}
        </span>
      )
    },
    {
      title:     'IGST Reversal',
      dataIndex: 'igstReversal',
      width:     130,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {fmtMoney(pickField(row, 'igst_reversal', 'igstReversal'))}
        </span>
      )
    },
    {
      title:     'Total ITC Reversal',
      dataIndex: 'totalItcReversal',
      width:     160,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {fmtMoney(readTotalReversal(row))}
        </span>
      )
    },
    {
      title:     'Status',
      dataIndex: 'reversalStatus',
      width:     140,
      render: (_v, row) => {
        const v = pickField(row, 'reversal_status', 'reversalStatus');
        if (!v) return (
          <Tag className="grn-status-tag" color="default">—</Tag>
        );
        return (
          <Tag color={reversalStatusColor(v)} className="grn-status-tag">
            {reversalStatusLabel(v) || String(v).replace(/_/g, ' ')}
          </Tag>
        );
      }
    }
  ]), [openParent]);

  // ── Render ───────────────────────────────────────────────────
  if (!canView && !canViewReports) {
    return (
      <div className="grn-shell">
        <Alert
          type="warning"
          showIcon
          message="You don't have permission to view ITC reversals."
          description="Ask your master account to grant erp.purchase.returns.view or erp.purchase.returns.reports.view."
        />
      </div>
    );
  }

  return (
    <div className="grn-shell">
      {/* Header strip */}
      <div className="grn-headstrip">
        <div className="grn-headstrip__lead">
          <span className="grn-headstrip__icon"><BankOutlined /></span>
          <div className="grn-headstrip__text">
            <span className="grn-headstrip__title">ITC Reversals</span>
            <span className="grn-headstrip__subtitle">
              Input tax credit reversed through purchase returns. Backend
              derives each reversal at post time from the original
              purchase-bill ITC claim, and flips rows to Cancelled when
              the parent return is cancelled. This surface is read-only.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="grn-stat-chip">
            <strong>{total}</strong> total
          </span>
        </Space>
      </div>

      {/* Sticky toolbar */}
      <div className="grn-toolbar">
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Supplier GSTIN…"
          value={gstinInput}
          onChange={(e) => setGstinInput((e.target.value || '').toUpperCase())}
          onPressEnter={(e) => {
            const v = (e.target.value || '').toUpperCase();
            setGstinInput(v);
            setGstin(v);
            setPage(1);
          }}
          style={{ width: 240 }}
        />
        <RangePicker
          size="small"
          value={dateRange}
          onChange={onDates}
          format={DATE_FMT}
          allowClear
          placeholder={['From', 'To']}
          style={{ width: 240 }}
        />
        <Tooltip title="Clear filters">
          <Button size="small" onClick={onReset}>Clear</Button>
        </Tooltip>
        <div className="grn-toolbar__spacer" />
        <Tooltip title="Refresh">
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={load}
          />
        </Tooltip>
      </div>

      {/* Grid card */}
      <Card className="grn-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            className="grn-grid"
            size="small"
            rowKey={(r) =>
              pickField(r, 'id', 'id')
              || (
                String(pickField(r, 'debit_note_no', 'debitNoteNo') || '')
                + '|'
                + String(pickField(r, 'purchase_return_line_id', 'purchaseReturnLineId') || '')
              )}
            dataSource={rows}
            columns={columns}
            sticky
            scroll={{ x: 'max-content' }}
            onRow={(row) => ({
              onDoubleClick: () => openParent(row)
            })}
            pagination={{
              current:           page,
              pageSize,
              total,
              showSizeChanger:   true,
              pageSizeOptions:   PAGE_SIZE_OPTIONS,
              showQuickJumper:   total > pageSize,
              size:              'small',
              showTotal: (count, range) =>
                count === 0
                  ? 'No rows'
                  : `${range[0]}–${range[1]} of ${count}`,
              onChange: (nextPage, nextSize) => {
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                } else {
                  setPage(nextPage);
                }
              }
            }}
            locale={{
              emptyText: loading
                ? ' '
                : 'No ITC reversals yet.'
            }}
          />
        </Spin>
      </Card>
    </div>
  );
}
